import { ExternalLink } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import SectionPlaceholder from "@/components/SectionPlaceholder";

const PUBLIC_DOMAIN = [
  { key: "b1", urlKey: "books.s3.b1.url" },
  { key: "b2", urlKey: "books.s3.b2.url" },
  { key: "b3", urlKey: "books.s3.b3.url" },
];

const MODERN = ["books.s2.b1", "books.s2.b2", "books.s2.b3", "books.s2.b4", "books.s2.b5"];
const QUEST = ["books.s4.b1", "books.s4.b2", "books.s4.b3", "books.s4.b4"];

export default function Books() {
  const { t } = useT();
  const title = t("books.title");
  const eyebrow = t("books.eyebrow");
  const description = t("books.intro");

  useSEO({
    title: `${title} | StoicOs`,
    description,
    canonical: "https://stoicos.ai/my-books",
    og: { title: `${title} | StoicOs`, description },
    jsonLd: [
      webPage({ name: title, description, url: "/my-books" }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: title, url: "/my-books" },
      ]),
    ],
  });

  return (
    <SectionPlaceholder
      source="books"
      eyebrow={eyebrow}
      title={title}
      subtitleLines={[t("books.subtitle")]}
      intro={t("books.intro")}
      comingSoon={t("books.comingSoon")}
      otherSections={[
        { to: "/stoic-journal",        labelKey: "nav.journal",   testid: "books-explore-journal" },
        { to: "/stoic-training-cards", labelKey: "nav.cards",     testid: "books-explore-cards" },
        { to: "/my-insights",          labelKey: "nav.insights",  testid: "books-explore-insights" },
        { to: "/about",                labelKey: "nav.about",     testid: "books-explore-about" },
      ]}
    >
      {/* SECTION 1 — ANCIENT WORKS */}
      <section className="mb-16" data-testid="books-ancient">
        <h2 className="font-inscription text-2xl md:text-3xl text-bone tracking-[0.06em] uppercase">
          {t("books.s1.title")}
        </h2>
        <p className="mt-3 text-fog font-light leading-relaxed max-w-xl">{t("books.s1.desc")}</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {["b1", "b2"].map((id) => (
            <article
              key={id}
              data-testid={`books-ancient-${id}`}
              className="p-6 border border-white/10 bg-ink-elevated/30"
            >
              <h3 className="font-serif text-2xl text-bone leading-tight">{t(`books.s1.${id}.title`)}</h3>
              <p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-gold">{t(`books.s1.${id}.year`)}</p>
              <p className="mt-4 text-bone/80 font-reading leading-[1.8]">{t(`books.s1.${id}.note`)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* SECTION 2 — MODERN STOICISM */}
      <section className="mb-16" data-testid="books-modern">
        <h2 className="font-inscription text-2xl md:text-3xl text-bone tracking-[0.06em] uppercase">
          {t("books.s2.title")}
        </h2>
        <p className="mt-3 text-fog font-light leading-relaxed max-w-xl">{t("books.s2.desc")}</p>
        <ul className="mt-8 space-y-3">
          {MODERN.map((k) => (
            <li
              key={k}
              data-testid={`books-modern-${k.split(".").pop()}`}
              className="pl-5 border-l border-gold/30 text-bone/85 font-reading leading-relaxed"
            >
              {t(k)}
            </li>
          ))}
        </ul>
      </section>

      {/* SECTION 3 — ESSENTIAL STOIC TEXTS */}
      <section className="mb-16" data-testid="books-essential">
        <h2 className="font-inscription text-2xl md:text-3xl text-bone tracking-[0.06em] uppercase">
          {t("books.s3.title")}
        </h2>
        <p className="mt-3 text-fog font-light leading-relaxed max-w-xl">{t("books.s3.desc")}</p>
        <ul className="mt-8 space-y-6">
          {PUBLIC_DOMAIN.map(({ key, urlKey }) => (
            <li
              key={key}
              data-testid={`books-essential-${key}`}
              className="p-6 border border-white/10 bg-ink-elevated/30"
            >
              <h3 className="font-serif text-2xl text-bone leading-tight">{t(`books.s3.${key}.title`)}</h3>
              <p className="mt-1 text-[11px] tracking-[0.22em] uppercase text-fog/80">{t(`books.s3.${key}.author`)}</p>
              <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-gold">{t("books.s3.whyMatters")}</p>
              <p className="mt-2 text-bone/85 font-reading leading-[1.85]">{t(`books.s3.${key}.why`)}</p>
              <a
                href={t(urlKey)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`books-essential-${key}-link`}
                className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold hover:text-gold-hover transition-colors"
              >
                {t("books.s3.readOnline")} <ExternalLink size={12} strokeWidth={1.5} />
              </a>
            </li>
          ))}
        </ul>
      </section>

      {/* SECTION 4 — ON MY QUEST */}
      <section data-testid="books-quest">
        <h2 className="font-inscription text-2xl md:text-3xl text-bone tracking-[0.06em] uppercase">
          {t("books.s4.title")}
        </h2>
        <p className="mt-3 text-fog font-light leading-relaxed max-w-xl">{t("books.s4.desc")}</p>
        <ul className="mt-8 space-y-3">
          {QUEST.map((k) => (
            <li
              key={k}
              data-testid={`books-quest-${k.split(".").pop()}`}
              className="pl-5 border-l border-gold/20 text-bone/85 font-reading italic leading-relaxed"
            >
              {t(k)}
            </li>
          ))}
        </ul>
      </section>
    </SectionPlaceholder>
  );
}
